import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/auth";
import BackToSiteBadge from "@/components/site/BackToSiteBadge";

/** Public landing page for the affiliate program. */
export default function AffiliateLanding() {
  const [terms, setTerms] = useState(null);

  useEffect(() => {
    axios.get(`${API}/affiliate/terms`).then((r) => setTerms(r.data)).catch(() => {});
  }, []);

  const feeUsd = ((terms?.settings?.enrollment_fee_cents || 299) / 100).toFixed(2);
  const commissionPct = ((terms?.settings?.commission_rate_bps || 500) / 100).toFixed(0);
  const attributionDays = terms?.settings?.attribution_window_days || 30;
  const minPayout = ((terms?.settings?.min_payout_cents || 500) / 100).toFixed(0);
  const refundHold = terms?.settings?.refund_hold_days || 30;

  return (
    <div className="min-h-screen bg-[var(--cream)] text-[var(--ink)]">
      {/* Nav */}
      <nav className="px-6 md:px-10 py-6 flex items-center justify-between border-b border-[rgba(15,31,23,0.08)]">
        <BackToSiteBadge />
        <Link to="/affiliate/signup" className="px-5 py-2.5 rounded-full bg-[var(--marigold)] text-white text-sm font-medium hover:bg-[#d14d00] transition" data-testid="aff-nav-join">
          Become an affiliate
        </Link>
      </nav>

      {/* Hero */}
      <section className="px-6 md:px-10 pt-16 pb-20 max-w-5xl mx-auto">
        <p className="font-mono-caption tracking-[0.3em] uppercase text-xs text-[var(--marigold)] mb-6" data-testid="aff-hero-eyebrow">
          Nutriora Affiliate Program · USD
        </p>
        <h1 className="font-display text-5xl md:text-6xl leading-tight tracking-tight mb-6">
          Share the ebook. <span className="font-editorial-italic text-[var(--marigold)]">Earn {commissionPct}%</span> on every sale.
        </h1>
        <p className="text-lg text-[var(--muted)] max-w-2xl mb-10">
          A one-time ${feeUsd} enrollment keeps the program honest. From there, every $3 sale that follows your link within {attributionDays} days earns you a transparent, server-verified commission.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Link to="/affiliate/signup" className="inline-block px-7 py-4 rounded-full bg-[var(--marigold)] text-white font-medium text-base hover:bg-[#d14d00] transition" data-testid="aff-hero-cta">
            Join for ${feeUsd}
          </Link>
          <Link to="/affiliate/terms" className="inline-block px-7 py-4 rounded-full border border-[rgba(15,31,23,0.2)] text-[var(--ink)] font-medium text-base hover:bg-white/50 transition" data-testid="aff-hero-terms">
            Read full terms
          </Link>
        </div>
      </section>

      {/* How it works */}
      <section className="px-6 md:px-10 py-16 bg-white/40 border-y border-[rgba(15,31,23,0.08)]">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-3xl md:text-4xl mb-10">How it works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { n: "01", t: "Enroll for $2.99", d: `A one-time payment through Paddle. Your account activates automatically after our server confirms the payment event — not before.` },
              { n: "02", t: "Share your link", d: `You get a short code (NUT-XXXXXX), a referral URL, a WhatsApp share, and a QR code. Share anywhere you'd normally share a product recommendation.` },
              { n: "03", t: "Earn 5% monthly", d: `Every $3 sale within ${attributionDays} days of a click earns you 5%. Payouts are monthly, minimum $${minPayout}, with a ${refundHold}-day refund hold.` },
            ].map((s) => (
              <div key={s.n} className="relative">
                <div className="font-mono-caption text-xs tracking-[0.3em] uppercase text-[var(--marigold)] mb-3">{s.n}</div>
                <h3 className="font-display text-xl mb-2">{s.t}</h3>
                <p className="text-sm text-[var(--muted)] leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The fine print — honest */}
      <section className="px-6 md:px-10 py-16 max-w-4xl mx-auto">
        <h2 className="font-display text-3xl md:text-4xl mb-8">What we promise. What we don't.</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="p-6 rounded-2xl bg-[var(--jade)]/10 border border-[var(--jade)]/20">
            <h3 className="font-display text-lg mb-3 text-[var(--jade)]">We do</h3>
            <ul className="space-y-2 text-sm text-[var(--ink)]">
              <li>• Pay 5% of eligible product revenue after discounts</li>
              <li>• Attribute the latest valid referral within {attributionDays} days</li>
              <li>• Freeze your commission rate at the moment of each sale — settings changes never alter past earnings</li>
              <li>• Hold commissions {refundHold} days then release monthly</li>
              <li>• Show masked payout details and require re-verification before any change</li>
            </ul>
          </div>
          <div className="p-6 rounded-2xl bg-[var(--marigold)]/10 border border-[var(--marigold)]/20">
            <h3 className="font-display text-lg mb-3 text-[var(--marigold)]">We don't</h3>
            <ul className="space-y-2 text-sm text-[var(--ink)]">
              <li>• Guarantee earnings</li>
              <li>• Pay commission on self-referrals</li>
              <li>• Pay commission for recruiting other affiliates</li>
              <li>• Deduct processing fees from your base</li>
              <li>• Permit misleading income or health claims</li>
            </ul>
          </div>
        </div>
      </section>

      <footer className="px-6 md:px-10 py-10 border-t border-[rgba(15,31,23,0.08)] text-center text-xs text-[var(--muted)] font-mono-caption tracking-[0.2em] uppercase">
        <Link to="/affiliate/terms" className="hover:text-[var(--marigold)]" data-testid="aff-footer-terms">Program terms</Link>
        <span className="mx-3">·</span>
        <Link to="/" className="hover:text-[var(--marigold)]" data-testid="aff-footer-home">Back to Milk2Income</Link>
      </footer>
    </div>
  );
}
